import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { components, DropdownIndicatorProps, MultiValue, SingleValue } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { Option } from '@connect-x-app/shared-types';
import { COLORS } from '@connect-x-app/shared-ui';
import { isSimpleTimeString } from '@connect-x-app/shared-utils';
import { INPUT_TEST_IDS } from '@connect-x-app/web-test-ids';
import { generateTimeOptions, mapToOption } from '@connect-x-app/web-utils';

import { Icon } from '../../icon';
import { ClearIndicator } from '../clearIndicator/ClearIndicator';
import { useInputError } from '../hooks/useInputError';
import { InputWrapper, InputWrapperProps } from '../InputWrapper';

import './timePicker.scss';

type TimePickerProps = InputWrapperProps & {
  minutesInterval?: number;
  onChange: (newValue: Option | null, name: string) => void;
  selectedValue: Option | null;
};

const DropdownIndicator = ({ ...props }: DropdownIndicatorProps<Option, false>) => (
  <components.DropdownIndicator {...props}>
    {props.children}
    <Icon colorName={COLORS.yuma.$01} name={'clockScheduleOutline'} size={2.4} testId={INPUT_TEST_IDS.timePicker.clock} />
  </components.DropdownIndicator>
);

export const TimePicker: FC<TimePickerProps> = ({ minutesInterval = 30, onChange, selectedValue, ...wrapperProps }) => {
  const { t } = useTranslation();
  const [customOption, setCustomOption] = useState<Option | null>(null);
  const { name, validation } = wrapperProps;
  const { setDirty, showError } = useInputError(validation);

  const internalOnChange = (newValue: SingleValue<Option> | MultiValue<Option>) => {
    onChange(newValue as SingleValue<Option>, name);
    setDirty();
  };

  const onCreateOption = (value: string) => {
    const newOption: Option = mapToOption(value);
    setCustomOption(newOption);
    onChange(newOption, name);
  };

  const generatedTimeOptions = useMemo(() => {
    return generateTimeOptions(minutesInterval);
  }, [minutesInterval]);

  const timeOptions = useMemo(() => {
    if (customOption) {
      return ([...generatedTimeOptions, customOption] as Option[]).sort((a: Option, b: Option) => a.label.localeCompare(b.label));
    }

    return generatedTimeOptions;
  }, [customOption, generatedTimeOptions]);

  const isValidNewOption = (value: string) => {
    return isSimpleTimeString(value) && !timeOptions.find((option: Option) => option.value === value);
  };

  return (
    <InputWrapper {...wrapperProps} showError={showError}>
      <CreatableSelect
        className={'time-picker'}
        classNamePrefix="time-picker"
        components={{
          ClearIndicator,
          DropdownIndicator,
        }}
        formatCreateLabel={(value: string) => t('TIME_PICKER.ADD', { value })}
        isClearable
        isValidNewOption={isValidNewOption}
        noOptionsMessage={() => t('TIME_PICKER.EMPTY_LIST')}
        onChange={internalOnChange}
        onCreateOption={onCreateOption}
        options={timeOptions}
        placeholder={t('TIME_PICKER.PLACEHOLDER')}
        value={selectedValue}
      />
    </InputWrapper>
  );
};
